import React from 'react'
import {
  HStack,
  Text,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react'
import {
  CheckCircleIcon,
  WarningIcon,
  TimeIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@chakra-ui/icons'
import { SubmissionList } from '../types/unified_models'

interface SubmissionSparkChartProps {
  submission: SubmissionList['submission_list'][0]
  isCompact?: boolean
}

interface SparkMetric {
  label: string
  value: string | number
  type: 'success' | 'warning' | 'error' | 'neutral'
  icon?: React.ElementType
  tooltip?: string
}

export default function SubmissionSparkChart({ submission, isCompact = false }: SubmissionSparkChartProps) {
  const mutedColor = useColorModeValue('gray.500', 'gray.400')
  const successColor = useColorModeValue('green.600', 'green.400')
  const warningColor = useColorModeValue('orange.600', 'orange.400')
  const errorColor = useColorModeValue('red.600', 'red.400')

  // Extract key metrics from submission results
  const getSparkMetrics = (): SparkMetric[] => {
    const metrics: SparkMetric[] = []
    const results = submission.submission_results || []

    // Runtime performance
    const runtimeResult = results.find(r => r.result_type === 'benchmark' && r.key === 'runtime')
    if (runtimeResult && runtimeResult.value_numeric) {
      const runtime = runtimeResult.value_numeric
      metrics.push({
        label: 'Runtime',
        value: `${runtime.toFixed(3)}s`,
        type: runtime < 1.0 ? 'success' : runtime < 5.0 ? 'warning' : 'error',
        icon: TimeIcon,
        tooltip: `Execution time: ${runtime.toFixed(3)} seconds`
      })
    }

    // Speedup/Performance improvement
    const speedupResult = results.find(r => r.result_type === 'benchmark' && (r.key === 'speedup' || r.key === 'improvement'))
    if (speedupResult && speedupResult.value_numeric) {
      const speedup = speedupResult.value_numeric
      metrics.push({
        label: 'Speedup',
        value: `${speedup.toFixed(2)}x`,
        type: speedup > 2.0 ? 'success' : speedup > 1.2 ? 'warning' : 'error',
        icon: speedup > 1.0 ? ChevronUpIcon : ChevronDownIcon,
        tooltip: `Performance improvement: ${speedup.toFixed(2)}x`
      })
    }

    // Correctness
    const correctnessResult = results.find(r => r.result_type === 'correctness')
    if (correctnessResult) {
      const passed = correctnessResult.value === 'passed' || correctnessResult.value === 'success'
      metrics.push({
        label: 'Tests',
        value: passed ? 'PASS' : 'FAIL',
        type: passed ? 'success' : 'error',
        icon: passed ? CheckCircleIcon : WarningIcon,
        tooltip: `Correctness tests: ${correctnessResult.value}`
      })
    }

    // Memory usage
    const memoryResult = results.find(r => r.result_type === 'benchmark' && r.key === 'memory')
    if (memoryResult && memoryResult.value_numeric) {
      const memoryMB = memoryResult.value_numeric / (1024 * 1024) // Convert bytes to MB
      metrics.push({
        label: 'Memory',
        value: `${memoryMB.toFixed(1)}MB`,
        type: memoryMB < 100 ? 'success' : memoryMB < 500 ? 'warning' : 'error',
        tooltip: `Memory usage: ${memoryMB.toFixed(1)} MB`
      })
    }

    // Inputs count is shown in SubmissionHistory via tags; avoid duplicating here

    // Analysis tools used (robust to different naming)
    const analysisTools = []
    const hasCilksan = results.some(r => r.result_type === 'cilksan' || r.result_type === 'analysis' && r.key === 'cilksan')
    const hasCilkscale = results.some(r => r.result_type === 'cilkscale' || r.result_type === 'cilkscale_benchmark' || (r.result_type === 'benchmark' && r.key === 'cilkscale'))
    const hasProfiler = results.some(r => r.result_type === 'profiler' || (r.result_type === 'analysis' && r.key === 'profiler'))
    if (hasCilksan) analysisTools.push('Cilksan')
    if (hasCilkscale) analysisTools.push('Cilkscale')
    if (hasProfiler) analysisTools.push('Profiler')
    
    if (analysisTools.length > 0) {
      metrics.push({
        label: 'Tools',
        value: analysisTools.length.toString(),
        type: 'neutral',
        tooltip: `Analysis tools used: ${analysisTools.join(', ')}`
      })
    }

    // If we don't have many metrics, add basic status info
    if (metrics.length === 0 && submission.status) {
      const statusPassed = submission.status === 'success' || submission.status === 'completed'
      metrics.push({
        label: 'Status',
        value: statusPassed ? 'OK' : 'FAIL',
        type: statusPassed ? 'success' : 'error',
        icon: statusPassed ? CheckCircleIcon : WarningIcon,
        tooltip: `Submission status: ${submission.status}`
      })
    }

    return metrics.slice(0, isCompact ? 3 : 5) // Limit metrics based on compact mode
  }

  const getColorForType = (type: SparkMetric['type']) => {
    switch (type) {
      case 'success': return successColor
      case 'warning': return warningColor
      case 'error': return errorColor
      default: return mutedColor
    }
  }

  const metrics = getSparkMetrics()

  if (metrics.length === 0) {
    // Show basic status information when no detailed metrics are available
    const statusColor = submission.status === 'success' ? successColor : 
                       submission.status === 'failed' ? errorColor : mutedColor
    
    return (
      <HStack spacing={2}>
        <Text fontSize="xs" color={statusColor} fontWeight="medium">
          {submission.status.toUpperCase()}
        </Text>
        <Text fontSize="xs" color={mutedColor}>
          • No detailed metrics
        </Text>
      </HStack>
    )
  }

  return (
    <HStack spacing={isCompact ? 1 : 2} wrap="wrap">
      {metrics.map((metric, index) => (
        <HStack key={index} spacing={1} cursor="help" title={metric.tooltip}>
          {metric.icon && (
            <Icon 
              as={metric.icon} 
              boxSize={2.5} 
              color={getColorForType(metric.type)}
            />
          )}
          <Text 
            fontSize="xs" 
            color={getColorForType(metric.type)}
            fontWeight="medium"
            minW="fit-content"
          >
            {metric.value}
          </Text>
          {!isCompact && (
            <Text fontSize="xs" color={mutedColor}>
              {metric.label}
            </Text>
          )}
        </HStack>
      ))}
    </HStack>
  )
} 